import { useEffect, useState } from "react";
import { Plus, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

const ROLES = ["super_admin", "admin", "operator", "pembimbing", "jamaah"];
const EMPTY = { name: "", email: "", phone: "", password: "", role: "operator" };

export default function AdminPengguna() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => axios.get(`${API}/admin/pengguna`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/admin/pengguna`, form);
      toast.success("Pengguna berhasil dibuat");
      setModalOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/admin/pengguna/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div data-testid="admin-pengguna-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Pengguna Sistem</h1>
          <p className="text-sm text-[#64748B]">Kelola akun administrator, operator, dan pembimbing.</p>
        </div>
        <Button data-testid="admin-pengguna-add-button" onClick={() => setModalOpen(true)} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]"><Plus className="h-4 w-4" /> Tambah Pengguna</Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Users2} title="Belum ada pengguna" description="Tambahkan pengguna sistem pertama." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-pengguna-table">
            <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead>Aktif</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`admin-pengguna-row-${u.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{u.name}</TableCell>
                  <TableCell className="text-[#64748B]">{u.email}</TableCell>
                  <TableCell className="capitalize text-[#64748B]">{u.role?.replace("_", " ")}</TableCell>
                  <TableCell>
                    <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`admin-pengguna-toggle-${u.id}`} disabled={u.id === user?.id} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="admin-pengguna-modal">
          <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} /></div>
              <div><Label>Nomor HP</Label><Input data-testid="user-form-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></div>
            </div>
            <div><Label>Password</Label><Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => update("password", e.target.value)} /></div>
            <div><Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => update("role", v)}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.filter((r) => user?.role === "super_admin" || !["super_admin", "admin"].includes(r)).map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="admin-pengguna-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
